import { getPdfUrl } from '@pharmsnap/pharmsnap-web/views/patent/util'
import { getCurrentInstance } from '@vue/composition-api'
import { PopupManager } from 'element-ui/lib/utils/popup'
import { ImagePanel } from 'patsnap-biz'
import { sharedCtx } from '../context'
import { downloadFileByUrl, openNewTab } from '../utils'
import { createIndependentVueInstance } from '../utils/vm'

/**
 * Extracts the file name from a given URL.
 *
 * @param url - The URL from which to extract the file name.
 * @returns The extracted file name, or an empty string if no file name is found.
 *
 * @example
 * // Returns "example.txt"
 * extractFileName("https://example.com/files/example.txt");
 *
 * @example
 * // Returns "image.jpg"
 * extractFileName("https://example.com/images/image.jpg");
 */
function extractFileName(url: string) {
  const regex = /\/([^/]+)$/
  const match = url.match(regex)
  return match ? match[1] : ''
}

async function logoBatchSignatureUrl(url: string) {
  if (!url) {
    return ''
  }
  const rt = await sharedCtx.service.search.logoBatchSignature([url], 'discovery_attachment')
  if (rt.length) {
    return rt[0]
  }
  return ''
}

export const attachmentTypeConfigMap: Record<string, { icon: string; action: 'download' | 'imgPreview' | 'openNewTab' }> = {
  'image/gif': {
    icon: 'FileImg',
    action: 'imgPreview',
  },
  'image/jpeg': {
    icon: 'FileImg',
    action: 'imgPreview',
  },
  'image/png': {
    icon: 'FileImg',
    action: 'imgPreview',
  },
  'application/pdf': {
    icon: 'FilePdf',
    action: 'openNewTab',
  },
  PDF: {
    icon: 'FilePdf',
    action: 'openNewTab',
  },
  'text/html': {
    icon: 'FileHtml',
    action: 'openNewTab',
  },
  HTML: {
    icon: 'FileHtml',
    action: 'openNewTab',
  },
  'application/x-zip-compressed': {
    icon: 'FileCommon',
    action: 'download',
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    // pptx
    icon: 'FileCommon',
    action: 'download',
  },
}

export function usePreviewAttachment() {
  const ins = getCurrentInstance()

  async function handlePreviewAttachment<T extends { attachment_type?: string; attachment_path?: string; page_number?: string }>(data: T) {
    if (!data.attachment_path) {
      return
    }
    const signatureUrl = await logoBatchSignatureUrl(data.attachment_path)
    if (data.attachment_type) {
      const action = attachmentTypeConfigMap[data.attachment_type]?.action
      if (action === 'imgPreview') {
        const imagePanelIns = createIndependentVueInstance({
          component: ImagePanel,
          componentVNodeData: {
            props: {
              imageData: {
                list: [
                  {
                    src: signatureUrl,
                    item: {
                      id: 1,
                      path: signatureUrl,
                    },
                  },
                ],
                total: 1,
                index: 0,
                type: 'image',
              },
              scale: {
                max: 100,
                min: 0.5,
                step: 0.2,
              },
              imagePanelShow: true,
            },
            style: {
              'z-index': PopupManager.nextZIndex(),
            },
            on: {
              'image-panel-close': () => {
                imagePanelIns.$destroy()
                imagePanelIns.$el.remove()
              },
            },
          },
          router: ins?.proxy.$router,
          i18n: ins?.proxy.$i18n,
        })
        return
      }
      if (action === 'openNewTab') {
        if (['PDF', 'application/pdf'].includes(data.attachment_type)) {
          openNewTab(getPdfUrl(signatureUrl, data.page_number))
          return
        }
        openNewTab(signatureUrl)
        return
      }
    }
    const fileName = extractFileName(data.attachment_path)
    await downloadFileByUrl(signatureUrl, fileName || `${Date.now()}`)
  }

  return {
    handlePreviewAttachment,
  }
}
